import { PhotoGalleryMigrationInfo, PhotoGalleryMigrationResult } from '../contexts';
import { FileCacheInterface } from '../FileSystem/FileCacheInterface';
import { FileSystemInterface } from '../FileSystem/FileSystemInterface';
import { safeFsOp } from '../lib/safeFsOp';

/**
 * One-shot migration from the pre-FileCache photo cache layout to the FileCache.
 *
 * The old layout split photos into two subdirs of the platform cache dir:
 *   `<cacheDirectory>photos/original/`  — original-resolution photos
 *   `<cacheDirectory>photos/scaled/`    — scaled variants
 *
 * The new FileCache holds everything in a single managed directory with LRU
 * eviction. Migration moves every legacy file into the FileCache (via
 * `addFile`), tolerates per-file failures (drops unreadable files), and
 * removes the legacy directories so {@link createLegacyPhotoMigration.needsMigration}
 * returns false on the next launch.
 *
 * This module is **deletable in its entirety** once every install has migrated.
 * Keep it isolated so removal is a one-file delete + a couple of import edits.
 */

export interface LegacyPhotoMigration {
  /** Fast probe: does the legacy layout still have files to migrate? Stat-only, no enumeration. */
  needsMigration: () => Promise<boolean>;
  /** Walk legacy files to count pending files and bytes. O(n) stat-only. */
  getMigrationInfo: () => Promise<PhotoGalleryMigrationInfo>;
  /** Move all legacy files into the FileCache, then delete the now-empty legacy directories. */
  migrate: (onProgress?: (progress: number) => void) => Promise<PhotoGalleryMigrationResult>;
}

export const createLegacyPhotoMigration = ({ fileSystem, fileCache, cacheDirectory }: {
  fileSystem: FileSystemInterface;
  fileCache: FileCacheInterface;
  /** Normalized cache root (trailing slash). Same value `createFileSystemPhotoGallery` resolves. */
  cacheDirectory: string;
}): LegacyPhotoMigration => {
  const legacyOriginalDir = `${cacheDirectory}photos/original/`;
  const legacyScaledDir = `${cacheDirectory}photos/scaled/`;
  const legacyDirs = [legacyOriginalDir, legacyScaledDir] as const;

  const legacyDirHasFiles = async (dir: string): Promise<boolean> => {
    if (!(await fileSystem.getDirectoryExists(dir))) return false;
    const { files } = await fileSystem.getDirectoryContents(dir);
    return files.length > 0;
  };

  const collectLegacyFiles = async (): Promise<string[]> => {
    const result: string[] = [];
    for (const dir of legacyDirs) {
      if (!(await fileSystem.getDirectoryExists(dir))) continue;
      const { files } = await fileSystem.getDirectoryContents(dir);
      for (const file of files) result.push(`${dir}${file}`);
    }
    return result;
  };

  return {
    needsMigration: async () => {
      for (const dir of legacyDirs) {
        if (await legacyDirHasFiles(dir)) return true;
      }
      return false;
    },

    getMigrationInfo: async () => {
      let fileCountToMigrate = 0;
      let bytesToMigrate = 0;
      for (const path of await collectLegacyFiles()) {
        fileCountToMigrate++;
        const info = await fileSystem.getFileInfo(path);
        bytesToMigrate += info.size ?? 0;
      }
      return { fileCountToMigrate, bytesToMigrate };
    },

    migrate: async onProgress => {
      const paths = await collectLegacyFiles();
      let filesMigrated = 0;
      let bytesMigrated = 0;
      for (let i = 0; i < paths.length; i++) {
        const path = paths[i];
        const ok = await safeFsOp(
          `migrate: failed to import ${path}`,
          async () => {
            const info = await fileSystem.getFileInfo(path);
            const size = info.size ?? 0;
            await fileCache.addFile(path);
            filesMigrated++;
            bytesMigrated += size;
          },
        );
        if (!ok) {
          // The file may be unreadable or already evicted by max-size pressure; either way,
          // best-effort cleanup so a retry of migrate() doesn't keep tripping over it.
          await safeFsOp(`migrate: failed to remove unmigrateable ${path}`, () => fileSystem.deleteAsync(path));
        }
        onProgress?.((i + 1) / paths.length);
      }
      // Remove the now-empty legacy directories so needsMigration() flips to false.
      for (const dir of legacyDirs) {
        await safeFsOp(`migrate: failed to remove legacy dir ${dir}`, async () => {
          if (await fileSystem.getDirectoryExists(dir)) await fileSystem.deleteDirectoryAsync(dir);
        });
      }
      return { filesMigrated, bytesMigrated };
    },
  };
};
